import React, { useState, useEffect } from 'react';
import { X, Minus, Plus, Loader2, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import reviewService from '../services/reviewService';

// Helper to extract numeric product ID from Shopify GID
const getProductId = (gid) => {
  if (!gid) return '';
  // Extract numeric ID from format: gid://shopify/Product/123456789
  const match = gid.match(/Product\/(\d+)/);
  return match ? match[1] : '';
};

const ProductDrawer = ({ 
  isOpen, 
  onClose, 
  product, 
  onAddToCart,
  loading,
  hideSubscriptions = false
}) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSellingPlan, setSelectedSellingPlan] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 0, reviewCount: 0, hasReviews: false });
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [hasMoreReviews, setHasMoreReviews] = useState(false);

  // Reset state when product changes
  useEffect(() => {
    if (product) {
      setSelectedVariant(product.variants?.[0] || null);
      setSelectedSellingPlan(null);
      setQuantity(1);
      setCurrentImageIndex(0);
      setReviews([]);
      setReviewsPage(1);
      setHasMoreReviews(false);
    }
  }, [product]);

  // Fetch review data when drawer opens
  useEffect(() => {
    const fetchReviews = async () => {
      if (isOpen && product) {
        const productId = getProductId(product.id);
        if (!productId) return;

        const badge = await reviewService.getReviewBadge(productId);
        setReviewData(badge);

        if (badge.hasReviews) {
          setReviewsLoading(true);
          try {
            const data = await reviewService.getProductReviews(productId, 1, 5);
            const fetched = data.reviews || [];
            setReviews(fetched);
            setHasMoreReviews(fetched.length === 5 && fetched.length < badge.reviewCount);
            setReviewsPage(1);
          } finally {
            setReviewsLoading(false);
          }
        } else {
          setReviews([]);
          setHasMoreReviews(false);
        }
      }
    };
    fetchReviews();
  }, [isOpen, product?.id]);

  const loadMoreReviews = async () => {
    const productId = getProductId(product?.id);
    if (!productId || reviewsLoading) return;
    setReviewsLoading(true);
    try {
      const nextPage = reviewsPage + 1;
      const data = await reviewService.getProductReviews(productId, nextPage, 5);
      const fetched = data.reviews || [];
      const combined = [...reviews, ...fetched];
      setReviews(combined);
      setReviewsPage(nextPage);
      setHasMoreReviews(fetched.length === 5 && combined.length < reviewData.reviewCount);
    } finally {
      setReviewsLoading(false);
    }
  };

  if (!isOpen || !product) return null;

  const images = product.images || [product.image];
  const hasMultipleImages = images.length > 1;
  const hasVariants = product.variants && product.variants.length > 1;
  
  // Get selling plans from the selected variant
  const variantSellingPlans = hideSubscriptions ? [] : (selectedVariant?.sellingPlans || []);
  const hasSubscription = variantSellingPlans.length > 0;

  // Get numeric product ID for Judge.me widget
  const productId = getProductId(product?.id);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating)
            ? 'fill-amber-400 text-amber-400'
            : i < rating
            ? 'fill-amber-400/50 text-amber-400'
            : 'fill-stone-200 text-stone-200'
        }`}
      />
    ));
  };

  // Get the current variant price
  const getVariantPrice = () => {
    return parseFloat(selectedVariant?.price || product.price || 0);
  };

  // Calculate display price based on selection
  const getDisplayPrice = () => {
    if (selectedSellingPlan?.subscriptionPrice) {
      return parseFloat(selectedSellingPlan.subscriptionPrice).toFixed(2);
    }
    if (selectedSellingPlan?.discountPercent) {
      const basePrice = getVariantPrice();
      const discount = parseFloat(selectedSellingPlan.discountPercent);
      return (basePrice * (1 - discount / 100)).toFixed(2);
    }
    return getVariantPrice().toFixed(2);
  };

  // Get subscription price for a plan
  const getSubscriptionPrice = (plan) => {
    if (plan.subscriptionPrice) {
      return parseFloat(plan.subscriptionPrice).toFixed(2);
    }
    const basePrice = getVariantPrice();
    const discount = parseFloat(plan.discountPercent || 0);
    return (basePrice * (1 - discount / 100)).toFixed(2);
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    
    setIsAdding(true);
    try {
      await onAddToCart({
        variantId: selectedVariant.id,
        quantity,
        sellingPlanId: selectedSellingPlan?.id || null
      });
      onClose();
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Filter selling plans to only show those with discounts (not test plans)
  const validSellingPlans = variantSellingPlans.filter(plan => 
    plan.discountPercent || plan.subscriptionPrice
  );

  return (
    <div className="fixed inset-0 z-[70]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-100">
          <span className="text-xs uppercase tracking-wider text-stone-400">
            {product.vendor}
          </span>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Image Gallery */}
          <div className="relative aspect-square bg-gradient-to-br from-stone-50 to-stone-100">
            <img
              src={images[currentImageIndex]}
              alt={product.title}
              className="w-full h-full object-contain p-8"
            />
            
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-stone-700" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-stone-700" />
                </button>
                
                {/* Image Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-stone-800' : 'bg-stone-300'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Product Info */}
          <div className="p-6">
            {/* Title & Rating */}
            <h2 className="text-2xl font-serif text-stone-800 mb-2">
              {product.title}
            </h2>
            
            {/* Judge.me Review Badge */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-0.5">
                {renderStars(reviewData.rating)}
              </div>
              {reviewData.hasReviews && (
                <span className="text-sm text-stone-400">
                  ({reviewData.reviewCount} reviews)
                </span>
              )}
            </div>

            {/* Subtitle */}
            {product.subtitle && (
              <p className="text-stone-600 mb-4 italic">
                {product.subtitle}
              </p>
            )}

            {/* Price Display */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl font-semibold text-stone-900">
                ${getDisplayPrice()}
              </span>
              {selectedSellingPlan && (selectedSellingPlan.discountPercent || selectedSellingPlan.subscriptionPrice) && (
                <>
                  <span className="text-lg text-stone-400 line-through">
                    ${getVariantPrice().toFixed(2)}
                  </span>
                  {selectedSellingPlan.discountPercent && (
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded">
                      Save {selectedSellingPlan.discountPercent}%
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Variants Selection */}
            {hasVariants && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-stone-700 mb-3">
                  Supply
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((variant) => (
                    <button
                      key={variant.id}
                      data-testid={`drawer-variant-${variant.title.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => {
                        setSelectedVariant(variant);
                        // Reset selling plan when variant changes
                        setSelectedSellingPlan(null);
                      }}
                      disabled={!variant.availableForSale}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        selectedVariant?.id === variant.id
                          ? 'border-stone-900 bg-stone-900 text-white'
                          : variant.availableForSale
                          ? 'border-stone-200 hover:border-stone-400 text-stone-700'
                          : 'border-stone-100 text-stone-300 cursor-not-allowed'
                      }`}
                    >
                      {variant.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-stone-700 mb-3">
                Quantity
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-lg border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                >
                  <Minus className="w-4 h-4 text-stone-600" />
                </button>
                <span className="w-12 text-center text-lg font-medium text-stone-800">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-lg border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                >
                  <Plus className="w-4 h-4 text-stone-600" />
                </button>
              </div>
            </div>

            {/* Purchase Options (Subscription) */}
            {hasSubscription && validSellingPlans.length > 0 && (
              <div className="mb-6 p-4 bg-stone-50 rounded-xl">
                <label className="block text-sm font-medium text-stone-700 mb-3">
                  Purchase Options
                </label>
                
                {/* One-time purchase */}
                <button
                  onClick={() => setSelectedSellingPlan(null)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border mb-2 transition-all ${
                    !selectedSellingPlan
                      ? 'border-stone-900 bg-white shadow-sm'
                      : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      !selectedSellingPlan ? 'border-stone-900' : 'border-stone-300'
                    }`}>
                      {!selectedSellingPlan && (
                        <div className="w-2.5 h-2.5 rounded-full bg-stone-900" />
                      )}
                    </div>
                    <span className="font-medium text-stone-800">One Time Purchase</span>
                  </div>
                  <span className="font-semibold text-stone-900">
                    ${getVariantPrice().toFixed(2)}
                  </span>
                </button>

                {/* Subscribe & Save Header */}
                <div className="mt-4 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-emerald-700">Subscribe & Save</span>
                  </div>
                </div>

                {/* Subscription options from variant's selling plans */}
                {validSellingPlans.map((plan) => (
                  <button
                    key={plan.id}
                    data-testid={`drawer-plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSelectedSellingPlan(plan)}
                    className={`w-full flex items-center justify-between p-4 rounded-lg border mb-2 transition-all ${
                      selectedSellingPlan?.id === plan.id
                        ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                        : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedSellingPlan?.id === plan.id ? 'border-emerald-500' : 'border-stone-300'
                      }`}>
                        {selectedSellingPlan?.id === plan.id && (
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        )}
                      </div>
                      <div className="text-left">
                        <span className="font-medium text-stone-800 block">{plan.name}</span>
                        <span className="text-xs text-stone-500">
                          (${getSubscriptionPrice(plan)}/delivery)
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {plan.discountPercent && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">
                          Save {plan.discountPercent}% OFF!
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Description */}
            {(product.description || product.descriptionHtml) && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-stone-700 mb-3">Description</h3>
                {product.descriptionHtml ? (
                  <div 
                    className="prose prose-sm prose-stone max-w-none
                      prose-p:my-2 prose-p:leading-relaxed
                      prose-ul:my-2 prose-ul:pl-5 prose-ul:list-disc
                      prose-ol:my-2 prose-ol:pl-5 prose-ol:list-decimal
                      prose-li:my-1
                      prose-headings:font-semibold prose-headings:text-stone-800
                      prose-strong:font-semibold prose-strong:text-stone-800
                      prose-a:text-emerald-600 prose-a:underline
                      text-stone-600"
                    dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                  />
                ) : (
                  <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-line">
                    {product.description}
                  </p>
                )}
              </div>
            )}

            {/* Customer Reviews Section */}
            <div className="mt-8 pt-6 border-t border-stone-100" data-testid="customer-reviews-section">
              <h3 className="text-lg font-semibold text-stone-800 mb-4">Customer Reviews</h3>
              
              {reviewData.hasReviews ? (
                <>
                  <div className="bg-stone-50 rounded-xl p-6 mb-4">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl font-bold text-stone-800">
                        {reviewData.rating.toFixed(1)}
                      </div>
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          {renderStars(reviewData.rating)}
                        </div>
                        <p className="text-sm text-stone-500">
                          Based on {reviewData.reviewCount} review{reviewData.reviewCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Individual Reviews */}
                  {reviewsLoading && reviews.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                    </div>
                  ) : reviews.length > 0 ? (
                    <div className="space-y-4" data-testid="reviews-list">
                      {reviews.map((review) => (
                        <div
                          key={review.id}
                          className="border border-stone-100 rounded-xl p-4"
                          data-testid={`review-${review.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-0.5">
                                {renderStars(review.rating)}
                              </div>
                              {review.verified && (
                                <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                  Verified Buyer
                                </span>
                              )}
                            </div>
                            {review.createdAt && (
                              <span className="text-xs text-stone-400">
                                {new Date(review.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            )}
                          </div>
                          {review.title && (
                            <h4 className="font-medium text-stone-800 mb-1">{review.title}</h4>
                          )}
                          {review.body && (
                            <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-line">
                              {review.body}
                            </p>
                          )}
                          <p className="text-xs text-stone-400 mt-2">
                            — {review.reviewer?.name || 'Anonymous'}
                          </p>
                        </div>
                      ))}

                      {hasMoreReviews && (
                        <button
                          onClick={loadMoreReviews}
                          disabled={reviewsLoading}
                          data-testid="load-more-reviews-btn"
                          className="w-full py-3 border border-stone-200 rounded-xl text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-50"
                        >
                          {reviewsLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading...
                            </span>
                          ) : (
                            'Load more reviews'
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500 text-center py-4">
                      Reviews are loading or unavailable.
                    </p>
                  )}
                </>
              ) : (
                <div className="bg-stone-50 rounded-xl p-6 text-center">
                  <div className="flex items-center justify-center gap-1 mb-2 opacity-30">
                    {renderStars(0)}
                  </div>
                  <p className="text-sm text-stone-500">
                    No reviews yet. Be the first to review this product!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Add to Cart */}
        <div className="p-6 border-t border-stone-100 bg-white">
          <button
            onClick={handleAddToCart}
            disabled={isAdding || !selectedVariant?.availableForSale}
            className="w-full py-4 bg-stone-900 hover:bg-stone-800 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAdding ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Add to Cart - ${(parseFloat(getDisplayPrice()) * quantity).toFixed(2)}
              </>
            )}
          </button>
          
          {selectedSellingPlan && (
            <p className="text-xs text-center text-stone-400 mt-3">
              Subscription will be charged {selectedSellingPlan.name.toLowerCase()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDrawer;
